import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CSVLink } from "react-csv";
import toast from "react-hot-toast";
import { Download } from "lucide-react";

import DataTable from "../../components/data-table/DataTable";
import EmptyState from "../../components/data-table/EmptyState";
import DateRangeFilter from "../../components/DateRangeFilter";
import DeexPending from "../../components/DeexPending";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { API } from "../../constant/api";
import { useBusinesses } from "../../hooks/use-businesses";
import { useDateFilter } from "../../hooks/use-date-filter";
import useAuthAxios from "../../utils/baseAxios";
import { errorMessage } from "../../utils/errorMessage";
import { formatExportDate } from "../../utils/formatters";
import { formatAmount, formatCount } from "../../utils/wordFunctions";
import StatCard from "../dashboard/components/StatCard";
import ActivityDetailSheet from "./components/ActivityDetailSheet";
import { activityColumns } from "./components/activityColumns";
import { ActivityRow, normalizeActivity } from "./components/normalizeActivity";

const PAGE_SIZE = 10;
const EXPORT_LIMIT = 1000;
const ALL_PAGES = "all";

const Activity = () => {
  const axiosInstance = useAuthAxios();
  const { value: dateValue, setValue: setDateValue, range } = useDateFilter("");

  const [page, setPage] = useState(1);
  const [pageRef, setPageRef] = useState(ALL_PAGES);
  const [selected, setSelected] = useState<ActivityRow | null>(null);

  const selectedRefId = pageRef === ALL_PAGES ? "" : pageRef;

  const {
    businesses,
    businessId,
    error: businessesError,
    isLoading: loadingBusinesses,
  } = useBusinesses();

  const { data: business, error: businessError } = useQuery({
    queryKey: ["get-business", businessId, range.from, range.to],
    queryFn: async () => {
      const response = await axiosInstance.get(
        API.getBusiness(businessId!, range.from, range.to)
      );
      return response.data.data;
    },
    retry: 1,
    enabled: !!businessId,
  });

  const summary = business?.summary;

  const { data: paymentPages } = useQuery({
    queryKey: ["payment-pages-filter", businessId],
    queryFn: async () => {
      const response = await axiosInstance.get(
        API.getPaymentPages(businessId!, 1, "")
      );
      return response?.data?.data?.pages ?? [];
    },
    retry: 1,
    enabled: !!businessId,
  });

  const fetchActivities = async (limit: number, requestedPage: number) => {
    if (selectedRefId) {
      const response = await axiosInstance.get(
        API.getPaymentTxn(selectedRefId, range.from, range.to, limit, requestedPage)
      );
      return response?.data;
    }
    const response = await axiosInstance.get(
      API.getTransaction(businessId!, requestedPage, range.from, range.to, limit)
    );
    return response?.data;
  };

  const {
    data: activities,
    error: activitiesError,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      "activity",
      businessId,
      selectedRefId,
      range.from,
      range.to,
      page,
    ],
    queryFn: () => fetchActivities(PAGE_SIZE, page),
    retry: 1,
    enabled: !!businessId,
  });

  const totalItems = selectedRefId
    ? activities?.data?.pagination?.totalDeposits ?? 0
    : activities?.data?.meta?.totalTransactions ?? 0;
  const totalPages = selectedRefId
    ? activities?.data?.pagination?.totalPages ?? 0
    : activities?.data?.meta?.totalPages ?? 0;

  // Separate request just for the CSV, capped so a busy account can't ask for
  // tens of thousands of rows in one go.
  const { data: exportSource } = useQuery({
    queryKey: [
      "activity-export",
      businessId,
      selectedRefId,
      range.from,
      range.to,
      totalItems,
    ],
    queryFn: () => fetchActivities(Math.min(totalItems, EXPORT_LIMIT), 1),
    retry: 1,
    enabled: !!businessId && totalItems > 0,
  });

  useEffect(() => {
    const error = (businessesError ?? businessError ?? activitiesError) as any;
    if (error) {
      toast.error(errorMessage(error?.message || error?.data?.message));
    }
  }, [businessesError, businessError, activitiesError]);

  const rows = useMemo(
    () =>
      normalizeActivity(
        selectedRefId
          ? activities?.data?.deposits
          : activities?.data?.transactions,
        !!selectedRefId
      ),
    [activities, selectedRefId]
  );

  const exportData = useMemo(() => {
    const source = normalizeActivity(
      selectedRefId
        ? exportSource?.data?.deposits
        : exportSource?.data?.transactions,
      !!selectedRefId
    );
    return source.map((row) => ({
      date: formatExportDate(row.date),
      type: row.type,
      details: row.description,
      amount: row.amountLabel,
      amountInUsd: row.usdLabel ?? "",
      amountInNaira: row.nairaLabel,
      status: row.status,
    }));
  }, [exportSource, selectedRefId]);

  return (
    <div className="mx-auto w-full max-w-[1400px] font-manrope">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div>
          <h1 className="font-sora text-[24px] font-bold text-foreground">
            Activity
          </h1>
          <p className="mt-1 text-[14px] font-medium text-muted-foreground">
            Every payout and deposit across your business.
          </p>
        </div>

        <CSVLink
          data={exportData}
          filename="deexoptions-activity.csv"
          className="inline-flex h-10 shrink-0 items-center gap-2 rounded-xl bg-primary px-4 text-[13px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <Download className="size-4" />
          Export CSV
        </CSVLink>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 mds:grid-cols-2 lgss:grid-cols-3">
        <StatCard
          label="Total payouts received"
          value={`$${formatAmount(summary?.totalPayouts)}`}
          footer={[
            `${formatAmount(summary?.totalPayoutsInNaira)} NGN`,
            `${formatCount(summary?.payoutsCount)}`,
          ]}
          loading={loadingBusinesses}
        />
        <StatCard
          label="Pending"
          value={`$${formatAmount(summary?.pendingPayouts)}`}
          footer={[`${formatAmount(summary?.pendingPayoutsInNaira)} NGN`]}
          loading={loadingBusinesses}
        />
        <StatCard
          label="Failed"
          value={`$${formatAmount(summary?.failedPayouts)}`}
          footer={[`${formatAmount(summary?.failedPayoutsInNaira)} NGN`]}
          loading={loadingBusinesses}
        />
      </div>

      <div className="mt-8">
        <DataTable<ActivityRow>
          columns={activityColumns}
          data={rows}
          isLoading={isLoading || loadingBusinesses}
          isFetching={isFetching}
          onRowClick={setSelected}
          toolbar={
            <>
              <Select
                value={pageRef}
                onValueChange={(value) => {
                  setPageRef(value);
                  setPage(1);
                }}
              >
                <SelectTrigger className="h-9 w-full rounded-lg bg-background text-[13px] mds:w-[240px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="font-manrope">
                  <SelectItem value={ALL_PAGES}>All payment pages</SelectItem>
                  {paymentPages?.map((item: any) => (
                    <SelectItem key={item.reference} value={item.reference}>
                      {item.name} (#{item.reference})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <DateRangeFilter
                value={dateValue}
                onChange={(next) => {
                  setDateValue(next);
                  setPage(1);
                }}
              />
            </>
          }
          emptyState={
            <EmptyState
              title="No transactions yet"
              description="Payouts and deposits will appear here as your customers pay."
            />
          }
          pagination={{
            page,
            totalPages,
            totalItems,
            onPageChange: setPage,
          }}
        />
      </div>

      <ActivityDetailSheet
        activity={selected}
        open={!!selected}
        onOpenChange={(next) => !next && setSelected(null)}
      />

      {!loadingBusinesses && (!businesses || businesses.length < 1) ? (
        <DeexPending />
      ) : null}
    </div>
  );
};

export default Activity;
